import { useRef } from "react";
import InfiniteScroll from "react-infinite-scroller";
import { Stack } from "@certa/blocks/thanos";
import { Loader } from "@certa/blocks";
import { DateHeader } from "./DateHeader";
import { css } from "emotion";
import type { OptimisticId } from "../../util/optimisticMessage.utils";

type GroupedMessageItem = {
  date: string;
  message: {
    messageId: OptimisticId | number | null;
    [key: string]: any;
  };
};

type GroupedMessagesWrapperProps<T extends GroupedMessageItem> = {
  groupedOptions: T[][];
  "aria-label": string;
  getKey: (item: T) => string;
  getLabel: (item: T) => string;
  onClick: (item: T, itemIndex: number) => void;
  render: (item: T) => JSX.Element;
  loadMore?: () => void;
  hasMore?: boolean;
};

const GroupedMessagesWrapper = <T extends GroupedMessageItem>({
  groupedOptions,
  "aria-label": ariaLabel,
  getKey,
  getLabel,
  onClick,
  render,
  loadMore,
  hasMore
}: GroupedMessagesWrapperProps<T>) => {
  const listBoxRef = useRef<HTMLDivElement>(null);

  const handleClick = (item: T, index: number) => {
    onClick(item, index);
  };

  const items = groupedOptions.map(group => (
    <div key={group[0].date}>
      <DateHeader date={group[0].date} />
      <div>
        {group.map((item, index) => (
          <div
            key={getKey(item) || index}
            className={css({
              width: "100%"
            })}
          >
            <Stack
              role="listitem"
              aria-label={getLabel(item)}
              onClick={() => handleClick(item, index)}
              data-index={index}
            >
              {render(item)}
            </Stack>
          </div>
        ))}
      </div>
    </div>
  ));

  return (
    <div ref={listBoxRef} aria-label={ariaLabel} role="list">
      {loadMore ? (
        <InfiniteScroll
          pageStart={0}
          loadMore={loadMore}
          hasMore={hasMore}
          useWindow={false}
          loader={
            <Stack align="center" justify="center">
              <Loader />
            </Stack>
          }
          isReverse={true}
        >
          {items}
        </InfiniteScroll>
      ) : (
        items
      )}
    </div>
  );
};

export { GroupedMessagesWrapper };
